import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore, type User } from '@/stores/auth';
import type { LoginResponse } from '@/types/api';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<User>('/api/v1/me'),
  });
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      // Login goes through the json_login firewall – no Bearer required.
      const res = await fetch('/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(data.message ?? `Login fehlgeschlagen (${res.status})`);
      }
      return (await res.json()) as LoginResponse;
    },
    onSuccess: (data) => setSession(data),
  });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/auth/logout', { method: 'POST' }),
    onSettled: () => {
      clear();
      qc.clear();
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (input: { email: string; password: string; firstname: string; lastname: string }) =>
      api('/auth/register', { method: 'POST', body: input }),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (input: { email: string }) =>
      api('/auth/reset-password/request', { method: 'POST', body: input }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      api(`/auth/reset-password/reset/${encodeURIComponent(token)}`, { method: 'POST', body: { password } }),
  });
}
